import { useMemo } from 'react';

import orderBy from 'lodash/orderBy';

import { useEnvironmentGroupsQuery, useEnvironmentsQuery } from './__generated__/queries.generated';

function useEnvironmentGroups(teamId: string, skip = false) {
  const { data, loading, refetch } = useEnvironmentGroupsQuery({
    variables: { teamId },
    fetchPolicy: 'cache-first',
    skip: skip || !teamId,
  });

  return { environmentGroups: data?.environmentGroups || [], loading, refetch };
}

function useEnvironments(teamId: string, skip = false) {
  const { data, loading, refetch } = useEnvironmentsQuery({
    variables: { teamId },
    fetchPolicy: 'cache-first',
    skip: skip || !teamId,
  });

  return { environments: data?.environments || [], loading, refetch };
}

function useAllEnvironments(teamId: string, skip = false) {
  const {
    environmentGroups,
    loading: loadingEnvironmentGroups,
    refetch,
  } = useEnvironmentGroups(teamId, skip);
  const { environments, loading: loadingEnvironments } = useEnvironments(teamId, skip);

  const envsWithoutGroups = useMemo(
    () => environments.filter((env) => !environmentGroups.some((group) => group.name === env.name)),
    [environments, environmentGroups],
  );

  const loading = loadingEnvironmentGroups || loadingEnvironments;

  const orderedEnvironmentGroups = useMemo(() => {
    return orderBy([...environmentGroups, ...envsWithoutGroups], 'name', ['asc']);
  }, [environmentGroups, envsWithoutGroups]);

  return {
    envs: orderedEnvironmentGroups,
    loading,
    refetch,
  };
}

export { useEnvironmentGroups, useEnvironments, useAllEnvironments };
